import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import FlowNav from '../FlowNav/FlowNav';
import InfoBox from '../InfoBox/InfoBox';
import SectionGuidanceToggle from '../SectionGuidanceToggle/SectionGuidanceToggle';
import OrgSectionTrail from '../OrgSectionTrail/OrgSectionTrail';
import './OrganisationMainSection.scss';

/**
 * Shared Organisation main-section scaffold: step nav (Previous / Next) above title toolbar, guidance, white surface.
 */
function OrganisationMainSection({
  className,
  embedded,
  title,
  tooltipContent,
  guidance,
  guidanceColor,
  toolbarActions,
  children,
  showSurface,
  flowPrev,
  flowNext,
  flowPrevText,
  flowNextText,
  flowDisableNext
}) {
  const hasFlow = Boolean(flowPrev || flowNext);

  return (
    <div
      className={classNames(
        'organisation-main-section',
        embedded && 'organisation-main-section--embedded',
        className
      )}
    >
      {hasFlow ? (
        <div className="organisation-main-section__sticky">
          <div className="organisation-main-section__flow">
            <FlowNav
              placement="top"
              prev={flowPrev}
              next={flowNext}
              prevText={flowPrevText}
              nextText={flowNextText}
              disableNext={flowDisableNext}
              center={<OrgSectionTrail />}
            />
          </div>
        </div>
      ) : null}
      <header className="organisation-main-section__toolbar">
        <div className="organisation-main-section__title-block">
          {title ? <h1 className="organisation-main-section__heading">{title}</h1> : null}
          <SectionGuidanceToggle />
        </div>
        {toolbarActions ? <div className="organisation-main-section__actions">{toolbarActions}</div> : null}
      </header>

      {guidance ? (
        <InfoBox
          className="organisation-main-section__guidance"
          color={guidanceColor}
          guidance={guidance}
          message={tooltipContent}
        />
      ) : null}

      <div className="organisation-main-section__body">
        {showSurface ? <div className="organisation-main-section__surface">{children}</div> : children}
      </div>
    </div>
  );
}

OrganisationMainSection.propTypes = {
  className: PropTypes.string,
  /** Use inside cards / collapse panels: no full-viewport chrome */
  embedded: PropTypes.bool,
  title: PropTypes.string,
  tooltipContent: PropTypes.node,
  guidance: PropTypes.node,
  /** Accent for the guidance InfoBox (plan/dashboard screens use #5BC2E7). */
  guidanceColor: PropTypes.string,
  toolbarActions: PropTypes.node,
  children: PropTypes.node,
  showSurface: PropTypes.bool,
  flowPrev: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
  flowNext: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
  flowPrevText: PropTypes.string,
  flowNextText: PropTypes.string,
  flowDisableNext: PropTypes.bool
};

OrganisationMainSection.defaultProps = {
  className: null,
  embedded: false,
  title: null,
  tooltipContent: null,
  guidance: null,
  guidanceColor: '#5BC2E7',
  toolbarActions: null,
  children: null,
  showSurface: true,
  flowPrev: null,
  flowNext: null,
  flowPrevText: 'Previous',
  flowNextText: 'Next Step',
  flowDisableNext: false
};

export default OrganisationMainSection;
